"use client";

import Link from "next/link";
import { ArrowRight, CalendarDays, Compass, Handshake, Trophy, Users, type LucideIcon } from "lucide-react";
import { SiteHero, SectionHead, CtaBand, FullBleedPhoto } from "@/components/marketing/site-ui";
import { MatchScoreMock } from "@/components/marketing/site-mocks";
import { useSiteContent } from "@/components/marketing/use-site-content";

/**
 * The product hub.
 *
 * Not five identical cards in a grid. The first capability is given the room it deserves
 * with its real screen next to it, because matching is what the whole product rests on,
 * and the other four are a list underneath it. A grid of five equal tiles says all five
 * matter the same amount, which is both untrue and forgettable.
 */

const ICONS: Record<string, LucideIcon> = {
  discover: Compass,
  events: CalendarDays,
  communities: Users,
  opportunities: Handshake,
  reputation: Trophy,
};

const ORDER = ["events", "communities", "opportunities", "reputation"] as const;

export function FeaturesClient() {
  const c = useSiteContent();
  const page = c.featuresPage;

  return (
    <>
      <SiteHero
        eyebrow={page.eyebrow}
        title={page.title}
        lede={page.lede}
        primaryLabel={page.primary}
        secondaryHref="/pricing"
        secondaryLabel={page.secondary}
      />

      {/* Matching first, at full size, with its own screen. */}
      <section className="border-t border-neutral-200 bg-white py-16 md:py-24">
        <div className="gs-page grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="min-w-0">
            <p className="gs-eyebrow flex items-center gap-2 text-primary">
              <Compass className="h-4 w-4" aria-hidden />
              {c.features.discover.name}
            </p>
            <h2 className="gs-headline mt-3 text-balance text-neutral-900">{c.discoverPage.title.replace(/<\/?accent>/g, "")}</h2>
            <p className="gs-lede mt-4 gs-measure text-neutral-600">{c.discoverPage.lede}</p>
            <Link
              href="/features/discover"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary"
            >
              {page.explore}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
          </div>
          <div className="min-w-0">
            <MatchScoreMock />
          </div>
        </div>
      </section>

      {/* The other four as a list with a rule between them, not a card grid. */}
      <section className="py-16 md:py-24">
        <div className="gs-page">
          <SectionHead title={page.gridHeading} body={page.gridBody} />
          <div className="mt-10 divide-y divide-neutral-200 border-y border-neutral-200">
            {ORDER.map((key) => {
              const Icon = ICONS[key];
              const feature = c.features[key];
              return (
                <Link
                  key={key}
                  href={`/features/${key}`}
                  className="group flex flex-col gap-3 py-7 transition-colors hover:bg-white sm:flex-row sm:items-center sm:gap-8 sm:px-4 sm:-mx-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block gs-title text-neutral-900">{feature.name}</span>
                    <span className="mt-1 block text-[15px] leading-relaxed text-neutral-600">{feature.tagline}</span>
                  </span>
                  <ArrowRight
                    className="h-5 w-5 shrink-0 text-neutral-300 transition-all group-hover:translate-x-1 group-hover:text-primary"
                    aria-hidden
                  />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <FullBleedPhoto
        src="/images/dashboard-evening-venue.webp"
        alt={page.photoAlt}
        eyebrow={page.proofEyebrow}
        title={page.proofTitle}
        body={page.proofBody}
      />

      <div className="pt-16 md:pt-24">
        <CtaBand />
      </div>
    </>
  );
}
